import { type JSX, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  demoMailEinspielen,
  demoVorlagen,
  type Demoergebnis,
  type Demomail,
  type Demovorlage,
  type Demovorlagentext,
} from '../api/support';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

const ERGEBNISNAME: Readonly<Record<Demoergebnis['ergebnis'], string>> = {
  aufgenommen: 'als neuer Vorgang aufgenommen',
  angehaengt: 'an einen bestehenden Vorgang angehängt',
  aussortiert: 'aussortiert',
  doppelt: 'schon vorhanden',
};

/** Was bei leerem Feld gilt — die Vorgabe der Vorlage oder der Bestand. */
function vorgabeAbsender(v: Demovorlagentext | undefined): string {
  if (v === undefined) return '';
  if (v.schluessel === 'anfrage_privat' || v.schluessel === 'firmenanfrage') {
    return `leer: ein ${v.schluessel === 'anfrage_privat' ? 'Privat' : 'Firmen'}kunde aus dem Bestand, sonst ${v.absender}`;
  }
  if (v.schluessel === 'antwort_vorgang' || v.schluessel === 'abwesenheit') {
    return `leer: der Absender des jüngsten Vorgangs, sonst ${v.absender}`;
  }
  return `leer: ${v.absender}`;
}

/**
 * CRM-183: „Demo-Mail einspielen" — nur an einem Postfach mit
 * Demo-Anbindung. Die Mail nimmt denselben Weg wie eine Mail aus Microsoft
 * Graph: Aufnahme, Verlauf, Zuordnung, Aussortieren. Leere Felder füllt die
 * Vorlage; was hier steht, geht vor.
 */
export function DemoMail({
  postfach,
  bezeichnung,
  beiSchliessen,
}: {
  readonly postfach: string;
  readonly bezeichnung: string;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [vorlagen, setzeVorlagen] = useState<readonly Demovorlagentext[]>([]);
  const [vorlage, setzeVorlage] = useState<Demovorlage | ''>('anfrage_privat');
  const [felder, setzeFelder] = useState({ absender: '', absendername: '', betreff: '', text: '' });
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [ergebnis, setzeErgebnis] = useState<Demoergebnis | undefined>(undefined);

  useEffect(() => {
    demoVorlagen().then(setzeVorlagen, (ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }, []);

  const gewaehlt = vorlagen.find((v) => v.schluessel === vorlage);

  /* Betreff und Text der Vorlage stehen zum Ändern im Feld; die Antwort holt ihren Betreff (SV-Nummer) aus dem Bestand. */
  useEffect(() => {
    if (gewaehlt === undefined) return;
    setzeFelder({
      absender: '',
      absendername: '',
      betreff: gewaehlt.schluessel === 'antwort_vorgang' ? '' : gewaehlt.betreff,
      text: gewaehlt.text,
    });
  }, [gewaehlt]);

  const frei = vorlage === '';
  const vollstaendig = !frei || (felder.absender.trim() !== '' && felder.betreff.trim() !== '' && felder.text.trim() !== '');

  async function einspielen(): Promise<void> {
    setzeLaeuft(true);
    setzeErgebnis(undefined);
    try {
      const nurGefuellt = (wert: string): string | undefined => (wert.trim() === '' ? undefined : wert.trim());
      const mail: Demomail = {
        ...(frei ? {} : { vorlage }),
        ...(nurGefuellt(felder.absender) === undefined ? {} : { absender: felder.absender.trim() }),
        ...(nurGefuellt(felder.absendername) === undefined ? {} : { absendername: felder.absendername.trim() }),
        ...(nurGefuellt(felder.betreff) === undefined ? {} : { betreff: felder.betreff.trim() }),
        ...(nurGefuellt(felder.text) === undefined ? {} : { text: felder.text.trim() }),
      };
      setzeErgebnis(await demoMailEinspielen(postfach, mail));
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <Dialog titel="Demo-Mail einspielen" unterzeile={`${bezeichnung} · Demo-Anbindung`} beiSchliessen={beiSchliessen}>
      <p className="feld__hilfe">
        Die Mail kommt nicht aus dem Netz, sie läuft aber denselben Weg wie eine echte: Vorgang,
        Verlauf, Zuordnung zum Kunden, Aussortieren. Antworten gehen in den Demo-Ausgang und werden
        nie zugestellt.
      </p>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {ergebnis !== undefined && (
        <p className="hinweiszeile" role="status">
          Die Mail wurde {ERGEBNISNAME[ergebnis.ergebnis]}
          {ergebnis.nummer !== null && ` (${ergebnis.nummer})`}.
        </p>
      )}

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="demo-vorlage">
          Vorlage
        </label>
        <select
          id="demo-vorlage"
          value={vorlage}
          onChange={(e) => {
            setzeVorlage(e.target.value as Demovorlage | '');
            if (e.target.value === '') setzeFelder({ absender: '', absendername: '', betreff: '', text: '' });
          }}
        >
          {vorlagen.map((v) => (
            <option key={v.schluessel} value={v.schluessel}>
              {v.bezeichnung}
            </option>
          ))}
          <option value="">— freie Mail —</option>
        </select>
      </div>

      <div className="feldreihe">
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="demo-absender">
            Absender{frei ? '' : ' (optional)'}
          </label>
          <input
            id="demo-absender"
            type="email"
            value={felder.absender}
            placeholder={vorgabeAbsender(gewaehlt)}
            onChange={(e) => setzeFelder({ ...felder, absender: e.target.value })}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="demo-name">
            Name (optional)
          </label>
          <input
            id="demo-name"
            type="text"
            value={felder.absendername}
            onChange={(e) => setzeFelder({ ...felder, absendername: e.target.value })}
          />
        </div>
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="demo-betreff">
          Betreff
        </label>
        <input
          id="demo-betreff"
          type="text"
          value={felder.betreff}
          placeholder={vorlage === 'antwort_vorgang' ? 'leer: „AW: …" mit der SV-Nummer des jüngsten Vorgangs' : ''}
          onChange={(e) => setzeFelder({ ...felder, betreff: e.target.value })}
        />
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="demo-text">
          Text
        </label>
        <textarea
          id="demo-text"
          rows={6}
          value={felder.text}
          onChange={(e) => setzeFelder({ ...felder, text: e.target.value })}
        />
      </div>

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={beiSchliessen}>
          Schließen
        </button>
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={laeuft || !vollstaendig}
          onClick={() => void einspielen()}
        >
          Einspielen
        </button>
      </div>
    </Dialog>
  );
}
